const ACCESS_KEY = 'otpshield.access';
const REFRESH_KEY = 'otpshield.refresh';
const USER_KEY = 'otpshield.user';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'OPERATOR' | 'ACCOUNTANT';
  trustLevel: 'STANDARD' | 'ELEVATED';
  team?: 'GENERAL' | 'VISA';
  status?: 'ACTIVE' | 'SUSPENDED' | 'OFFBOARDED' | 'ARCHIVED';
  companyId: string;
  mustChangePassword?: boolean;
}

export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  getUser: (): User | null => {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as User;
    } catch {
      // Corrupted user payload — clear so we re-auth instead of crashing.
      localStorage.removeItem(USER_KEY);
      return null;
    }
  },
  set: (access: string, refresh: string, user: User) => {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    scheduleProactiveRefresh(access);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    cancelProactiveRefresh();
  },
};

// ── Proactive refresh ────────────────────────────────────────────────
//
// Before 2026-07-02, refresh only fired REACTIVELY after a 401. Under a
// dashboard with ~10 TanStack Query hooks all polling at 60s intervals,
// that produced synchronised 401 stampedes every ~15 min (JWT lifetime).
// Example from prod logs 2026-07-02 06:36 UTC: 98 x 401 on
// /unlogged-count + 57 x /auth/refresh in 20 seconds from two clients.
//
// Fix: decode the access token's `exp` claim on set/boot, and schedule a
// silent refresh 60s BEFORE expiry so the next poll uses a fresh token.
// If the tab is asleep past the timer, the fallback reactive path still
// covers it — this is a stampede-avoidance optimisation, not correctness.

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function decodeJwtExpMs(token: string): number | null {
  const [, payload] = token.split('.');
  if (!payload) return null;
  try {
    // Native atob works — access tokens are RS256 JWTs from Fastify.
    // Base64URL → base64 padding.
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    const claims = JSON.parse(json) as { exp?: number };
    return typeof claims.exp === 'number' ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

function cancelProactiveRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleProactiveRefresh(access: string) {
  cancelProactiveRefresh();
  const expMs = decodeJwtExpMs(access);
  if (expMs == null) return;
  // Fire 60s before expiry, minimum 5s from now. Cap at 30 min to guard
  // against setTimeout wrap-around on ridiculous exp values.
  const delay = Math.max(5_000, Math.min(30 * 60_000, expMs - Date.now() - 60_000));
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    void refreshAccessToken();
  }, delay);
}

// Kick off the timer on page load if we booted with a token in storage
// (survives full-page reloads without waiting for the first 401).
if (typeof window !== 'undefined') {
  const boot = localStorage.getItem(ACCESS_KEY);
  if (boot) scheduleProactiveRefresh(boot);
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message?: string) {
    // Prefer the API's `message` field when present so callers that surface
    // `err.message` in toasts get the human sentence
    // (e.g. "Shubham Jain just approved this request.") instead of the raw
    // "API error 409". The status is still on `err.status` for anyone who
    // needs it.
    const apiMessage =
      body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string'
        ? ((body as { message: string }).message)
        : null;
    super(message ?? apiMessage ?? `API error ${status}`);
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  noAuth?: boolean;
}

async function rawApi<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!opts.noAuth) {
    const t = tokenStore.getAccess();
    if (t) headers['Authorization'] = `Bearer ${t}`;
  }
  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

let refreshing: Promise<boolean> | null = null;

/**
 * Public entry point for non-api() callers (EventSource, WebSocket) that
 * hit 401 outside the fetch() wrapper. Returns true on success. Coalesces
 * concurrent callers onto a single in-flight refresh so ten SSE hooks
 * hitting 401 at once fire ONE /auth/refresh, not ten.
 *
 * Coalescing window is held for REFRESH_HOLD_MS after the promise
 * settles. Without this hold, pollers that hit 401 just AFTER the first
 * refresh completed each start their OWN refresh (see prod logs
 * 2026-07-02 06:36 UTC: 57 x /auth/refresh in 20s across two clients).
 * With it, callers arriving within 1s of the last successful refresh
 * short-circuit onto the same resolved promise.
 */
const REFRESH_HOLD_MS = 1_000;

export async function refreshAccessToken(): Promise<boolean> {
  refreshing ??= tryRefresh();
  const ok = await refreshing;
  // Debounce clear: let the resolved promise live long enough that
  // stragglers see `refreshing != null` and await the same result.
  const held = refreshing;
  setTimeout(() => {
    if (refreshing === held) refreshing = null;
  }, REFRESH_HOLD_MS);
  return ok;
}

async function tryRefresh(): Promise<boolean> {
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) return false;
  try {
    const r = await rawApi<{ accessToken: string; refreshToken: string }>('/api/auth/refresh', {
      method: 'POST',
      body: { refreshToken },
      noAuth: true,
    });
    const user = tokenStore.getUser();
    if (!user) return false;
    tokenStore.set(r.accessToken, r.refreshToken, user);
    return true;
  } catch {
    tokenStore.clear();
    return false;
  }
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  try {
    return await rawApi<T>(path, opts);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401 && !opts.noAuth) {
      // Route through the coalescing entry point so this call reuses any
      // in-flight OR recently-completed refresh instead of firing its own.
      const ok = await refreshAccessToken();
      if (ok) return rawApi<T>(path, opts);
      tokenStore.clear();
      window.location.href = '/login';
    }
    throw err;
  }
}

export const auth = {
  passwordLogin: (email: string, password: string) =>
    rawApi<{ accessToken: string; refreshToken: string; user: User }>(
      '/api/auth/password/login',
      { method: 'POST', body: { email, password, clientKind: 'WEB' }, noAuth: true },
    ),
  setPassword: (password: string) =>
    api<{ ok: true }>('/api/auth/password/set', { method: 'POST', body: { password } }),
  adminResetPassword: (userId: string, password: string) =>
    api<{ ok: true; userId: string }>('/api/auth/password/admin-reset', {
      method: 'POST',
      body: { userId, password },
    }),
  createUser: (input: {
    email: string;
    name: string;
    role: string;
    team?: string;
    trustLevel?: string;
    password?: string;
  }) =>
    api<{ id: string; email: string; name: string; role: string }>('/api/users', {
      method: 'POST',
      body: input,
    }),
  sendOtp: (email: string) =>
    rawApi<{ ok: true }>('/api/auth/email-otp/send', { method: 'POST', body: { email }, noAuth: true }),
  verifyOtp: (email: string, code: string) =>
    rawApi<{ accessToken: string; refreshToken: string; user: User }>(
      '/api/auth/email-otp/verify',
      { method: 'POST', body: { email, code, clientKind: 'WEB' }, noAuth: true },
    ),
  me: () => api<User>('/api/auth/me'),
  logout: async () => {
    const refreshToken = tokenStore.getRefresh();
    try {
      if (refreshToken) await rawApi('/api/auth/logout', { method: 'POST', body: { refreshToken }, noAuth: true });
    } catch {
      // ignore — logging out anyway
    }
    tokenStore.clear();
  },
};
